// ════════════════════════════════════════════════════════════════════════
// coFund.js: platform co-funded discounts ("Platform contribution").
// ════════════════════════════════════════════════════════════════════════
// A platform (aggregator) sometimes pays part of a discount the restaurant runs.
// An entry = channel + inclusive date range + the discount % the restaurant ran
// (discountPct) + the share of that discount the platform pays (cofundPct), both
// stored as FRACTIONS (0.45 = 45%). Per daily_logs ROW:
//   rawNet       = (typeof net==="number" && net>0) ? net : gross   (same as calcDaily)
//   baseNet      = gross>0 ? Math.min(rawNet, gross) : rawNet        (same as calcDaily)
//   discount     = max(0, gross − baseNet)
//   coFunded     = min(discount, gross × discountPct)
//   amount       = round2(coFunded × cofundPct)
//   effectiveNet = amount>0 ? baseNet + amount : net   (ORIGINAL net untouched otherwise)
// Callers pass effectiveNet as calcDaily's 2nd argument (use effNet(row)).
// A FED row (daily_logs.source 'feed') takes none: coFundApplies(row) is false, so
// effNet / coFundAmount read it as a row with no entry, whatever is stamped on it.
// calcDaily / calcItem / applyPeriodAdjustments are NOT touched. Per ROW only: no
// period top-up, no split across same-day rows. A date with no log row gets nothing.
// Overlapping entries for one channel are rejected at save; any residual overlap is
// resolved by ONE rule (compareCoFundPriority): latest dateFrom, then latest
// createdAt, then the larger id. Never summed.
//
// Normalised entry: { id, channelId, dateFrom, dateTo, discountPct, cofundPct, createdAt }
// Row stamp:        row.coFundRates = { id, discountPct, cofundPct } | null
//
// NO imports on purpose (scripts/calc-parity-check.cjs loads this file in a vm).
// KEEP IN LOCKSTEP with backend/lib/coFund.cjs (same math, same fed-row rule
// (coFundApplies), same % parsing and validation, same isMissingRelationError;
// the backend adds attachCoFundRates, this file adds sumCoFundByEntry).

export const COFUND_PCT_MIN = 0.10; // owner's co-fund range, inclusive (UI validation only)
export const COFUND_PCT_MAX = 0.50;

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;
export function isYmd(s) { return typeof s === "string" && YMD_RE.test(s); }

export function round2(n) { return +(Number(n) || 0).toFixed(2); }

function toNum(v) { return (v === null || v === undefined || v === "") ? NaN : Number(v); }

function validRates(r) {
  if (!r) return false;
  const dp = r.discountPct, cp = r.cofundPct;
  return typeof dp === "number" && typeof cp === "number" &&
    Number.isFinite(dp) && Number.isFinite(cp) &&
    dp > 0 && dp <= 1 && cp > 0 && cp <= 1;
}

// DB row (snake_case) -> normalised entry. Percent columns may arrive as numbers
// or numeric strings ("0.4500"); null/""/missing -> NaN (entry becomes unusable,
// never matches, but is still listed in the UI table).
export function normaliseCoFundRow(row) {
  if (!row) return null;
  return {
    id: row.id ?? null,
    channelId: row.channel_id ?? null,
    dateFrom: row.date_from != null ? String(row.date_from).slice(0, 10) : "",
    dateTo: row.date_to != null ? String(row.date_to).slice(0, 10) : "",
    discountPct: toNum(row.discount_pct),
    cofundPct: toNum(row.cofund_pct),
    createdAt: row.created_at ?? null,
  };
}

export function isUsableCoFundEntry(e) {
  return !!e && isYmd(e.dateFrom) && isYmd(e.dateTo) && e.dateTo >= e.dateFrom && validRates(e);
}

function createdMs(e) { const n = Date.parse(e && e.createdAt != null ? String(e.createdAt) : ""); return Number.isFinite(n) ? n : -Infinity; }

// > 0 when `a` beats `b`, < 0 when `b` beats `a`, 0 when indistinguishable.
export function compareCoFundPriority(a, b) {
  if (a.dateFrom !== b.dateFrom) return a.dateFrom > b.dateFrom ? 1 : -1;
  const ca = createdMs(a), cb = createdMs(b);
  if (ca !== cb) return ca > cb ? 1 : -1;
  const ia = String(a.id ?? ""), ib = String(b.id ?? "");
  if (ia !== ib) return ia > ib ? 1 : -1;
  return 0;
}

// The single winning usable entry covering `ymd` (inclusive), optionally for one
// channel. null when none. `ymd` MUST be the raw "YYYY-MM-DD" log_date string.
export function pickCoFundEntry(entries, ymd, channelId) {
  if (!Array.isArray(entries) || entries.length === 0 || !isYmd(ymd)) return null;
  let best = null;
  for (const e of entries) {
    if (!isUsableCoFundEntry(e)) continue;
    if (channelId != null && e.channelId !== channelId) continue;
    if (ymd < e.dateFrom || ymd > e.dateTo) continue;
    if (best === null || compareCoFundPriority(e, best) > 0) best = e;
  }
  return best;
}

// What gets stamped on a row: { id, discountPct, cofundPct } | null.
export function coFundRatesFor(entries, ymd, channelId) {
  const e = pickCoFundEntry(entries, ymd, channelId);
  return e ? { id: e.id, discountPct: e.discountPct, cofundPct: e.cofundPct } : null;
}

export function coFundBreakdown(gross, net, rates) {
  if (!validRates(rates) || typeof gross !== "number" || !Number.isFinite(gross)) {
    return { discount: 0, coFunded: 0, amount: 0, effectiveNet: net };
  }
  const rawNet = (typeof net === "number" && net > 0) ? net : gross;
  const baseNet = gross > 0 ? Math.min(rawNet, gross) : rawNet;
  const discount = Math.max(0, gross - baseNet);
  const coFunded = Math.min(discount, gross * rates.discountPct);
  const amount = round2(coFunded * rates.cofundPct);
  if (!(amount > 0)) return { discount, coFunded, amount: 0, effectiveNet: net };
  return { discount, coFunded, amount, effectiveNet: baseNet + amount };
}

// Co-funding stays OFF on fed rows (plan 7.3; migration 046 section 14): a row the
// integration service wrote (daily_logs.source 'feed') takes no Platform
// contribution, whatever entry covers its day. Only the exact string "feed" is
// exempt: 'manual', 'statement', 'scrape', null and a missing source (a select
// that doesn't read the column) keep co-funding exactly as before. The check is
// inlined, the same as isFedRow in frontend/src/lib/dailyLogFeed.js, so both
// twins stay import-free.
export function coFundApplies(row) { return !(row && row.source === "feed"); }

// THE helper for every calcDaily call site: calcDaily(r.gross, effNet(r), r.cpc, cfg).
// Rows without coFundRates (optimistic rows, demo rows, NaN guards) and fed rows
// (coFundApplies) -> r.net untouched.
export function effNet(row) { return row ? coFundBreakdown(row.gross, row.net, coFundApplies(row) ? row.coFundRates : null).effectiveNet : undefined; }
export function coFundAmount(row) { return row ? coFundBreakdown(row.gross, row.net, coFundApplies(row) ? row.coFundRates : null).amount : 0; }

// First entry on the same channel (other than excludeId) whose inclusive range
// overlaps cand's. Adjacent ranges (one ends the day before the next starts) do not overlap.
export function findCoFundOverlap(entries, cand, excludeId) {
  if (!Array.isArray(entries) || !cand || !isYmd(cand.dateFrom) || !isYmd(cand.dateTo)) return null;
  for (const e of entries) {
    if (!e || e.channelId !== cand.channelId) continue;
    if (excludeId != null && e.id === excludeId) continue;
    if (!isYmd(e.dateFrom) || !isYmd(e.dateTo)) continue;
    if (e.dateFrom <= cand.dateTo && e.dateTo >= cand.dateFrom) return e;
  }
  return null;
}

// UI whole-percent text -> stored fraction (4dp, NUMERIC(5,4)). Forgiving about how
// people type a percent: spaces around it, an optional trailing "%" ("45%", "45 %")
// and ONE comma as the decimal point ("12,5" = 12.5). Plain decimals only: "1e2",
// "0x10", "4 5", "1,2,3", "12,5.3" are junk. "" and junk -> NaN (validateCoFundInput
// tells them apart: blank -> "allRequired", junk -> "badNumber").
const PCT_TEXT_RE = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/;
export function pctInputToFraction(v) {
  let s = String(v ?? "").trim();
  if (s.endsWith("%")) s = s.slice(0, -1).trimEnd();
  if (!s.includes(".") && s.indexOf(",") === s.lastIndexOf(",")) s = s.replace(",", ".");
  if (!PCT_TEXT_RE.test(s)) return NaN;
  const n = Number(s);
  return Number.isFinite(n) ? +(n / 100).toFixed(4) : NaN;
}

// Stored fraction -> UI whole-percent string ("45", "12.35"). Invalid -> "".
export function fractionToPctInput(f) {
  const n = toNum(f);
  return Number.isFinite(n) ? String(+(n * 100).toFixed(2)) : "";
}

// A discount / co-fund value is EITHER a FRACTION (number, e.g. 0.45) OR the raw
// whole-percent TEXT the user typed (string, e.g. "45%", read by pctInputToFraction).
// -> { value: fraction | NaN, bad: true when text is there but is not a number }.
function pctFieldValue(v) {
  if (typeof v !== "string") return { value: v, bad: false };
  if (v.trim() === "") return { value: NaN, bad: false };
  const f = pctInputToFraction(v);
  return { value: f, bad: !Number.isFinite(f) };
}

// input = { channelId, dateFrom, dateTo, discountPct, cofundPct }; each % is a
// FRACTION (number) or the raw whole-percent TEXT (string, see pctFieldValue).
// Pass the TEXT to get "badNumber"; a NaN fraction can only say "allRequired".
// Returns null (ok) or, checked in this order: "allRequired" (something blank or
// missing) | "badNumber" (a % was typed but is not a number) | "endBeforeStart" |
// "discountRange" | "cofundRange" | "overlap".
export function validateCoFundInput(input, existing, excludeId) {
  const { channelId, dateFrom, dateTo } = input || {};
  const d = pctFieldValue((input || {}).discountPct), c = pctFieldValue((input || {}).cofundPct);
  const blank = (p) => !p.bad && !Number.isFinite(p.value);
  if (!channelId || !isYmd(dateFrom) || !isYmd(dateTo) || blank(d) || blank(c)) return "allRequired";
  if (d.bad || c.bad) return "badNumber";
  const discountPct = d.value, cofundPct = c.value;
  if (dateTo < dateFrom) return "endBeforeStart";
  if (!(discountPct > 0 && discountPct <= 1)) return "discountRange";
  if (!(cofundPct >= COFUND_PCT_MIN && cofundPct <= COFUND_PCT_MAX)) return "cofundRange";
  if (findCoFundOverlap(existing, { channelId, dateFrom, dateTo }, excludeId)) return "overlap";
  return null;
}

// FRONTEND-ONLY: platform contribution per entry id over already-stamped rows
// (a channel's history). Only rows whose WINNING entry is that id count, so the
// overlap rule is respected; a fed row adds nothing (coFundAmount is 0 on it).
// { [entryId]: amount2dp }.
export function sumCoFundByEntry(rows) {
  const out = {};
  for (const r of rows || []) {
    const id = r && r.coFundRates ? r.coFundRates.id : null;
    if (id == null) continue;
    const a = coFundAmount(r);
    if (a > 0) out[id] = round2((out[id] || 0) + a);
  }
  return out;
}

// True when a Supabase/PostgREST error means "platform_cofunding does not exist"
// (migration 045 not applied, or dropped during a rollback). PostgREST 12+ answers
// PGRST205 ("Could not find the table … in the schema cache"); older PostgREST /
// raw Postgres answer 42P01 (undefined_table). A missing table PROVES there are no
// co-fund entries, so callers may treat it as an empty list: the numbers stay
// correct. Every OTHER error means the entries could not be read.
export function isMissingRelationError(err) {
  const c = err && err.code != null ? String(err.code) : "";
  return c === "PGRST205" || c === "42P01";
}
